import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  AssignContractorsToCustomer,
  FetchContractorsAvailableForAssign,
} from 'src/constants/actionTypes'
import { contractorPageSizeOptions } from 'src/constants/contractor/contractorGrid'
import {
  AssignContractorsTableContext,
  IAssignContractorsTableContext,
} from './AssignContractorsTable.context'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getSelectedMarket } from 'src/selectors/market'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { AssignContractorsHeader } from './header/AssignContractorsHeader'
import tw from 'twin.macro'
import Typography from '@mui/material/Typography'
import { colors } from 'src/theme'
import { CirclePlusMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import {
  assignContractorsColumns,
  assignContractorsPageSizeOptions,
} from 'src/constants/linkSubcontractor/assignContractorsGrid'
import { EmptyAssignContractors } from './EmptyAssignContractors'
import { LinkableSubcontractor } from 'src/interfaces/subcontractor'
import { showToast } from 'src/utils/toast'

interface FetchContractorsAvailableForAssignParams {
  limit?: number
  offset?: number
  searchQuery: string
  tagsFilter: Array<string>
  sortModel: GridSortModel
}

interface Props {
  refreshLinkedContractors: () => void
  selectedMCCustomerId: string
}

const getRowId = (row: LinkableSubcontractor) => row.id

const AssignContractorsTableComponent = ({
  refreshLinkedContractors,
  selectedMCCustomerId,
}: Props) => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(contractorPageSizeOptions)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [contractors, setContractors] = useState<Array<LinkableSubcontractor>>([])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedContractorIds, setSelectedContractorIds] = useState<Array<string | number>>([])

  const [totalContractors, setTotalContractors] = useState<number>(0)
  const [tagsFilter, setTagsFilter] = useState<Array<string>>([])
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'firstName',
      sort: 'desc',
    },
  ])

  const fetchContractorsAvailableForAssign = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
    }: FetchContractorsAvailableForAssignParams) => {
      setIsLoading(true)

      try {
        const params: any = {
          limit,
          offset,
          customerId: selectedMCCustomerId,
        }

        if (searchQuery) {
          params.contractorSearch = searchQuery
        }

        if (selectedMarket?.id) {
          params.partnerMarketId = selectedMarket?.id
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
        }

        const payload: { data: Array<LinkableSubcontractor>; pagination: Pagination } =
          await createAsyncAction(dispatch, FetchContractorsAvailableForAssign.request(params))

        setContractors(payload.data)
        setTotalContractors(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [selectedMarket?.id],
  )

  const refreshContractorsAvailableForAssign = useCallback(async () => {
    await fetchContractorsAvailableForAssign({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
    })
  }, [limit, offset, searchQuery, sortModel, tagsFilter, fetchContractorsAvailableForAssign])

  useEffect(() => {
    fetchContractorsAvailableForAssign({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
    })
  }, [limit, offset, searchQuery, sortModel, tagsFilter, selectedMarket?.id])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const handleRowSelectionChange = useCallback((newSelection: GridRowSelectionModel) => {
    setSelectedContractorIds(newSelection)
  }, [])

  const assignContractorsContextValue = useMemo<IAssignContractorsTableContext>(() => {
    return {
      tagsFilter,
      setTagsFilter,

      searchQuery,
      setSearchQuery,
    }
  }, [searchQuery, setSearchQuery, tagsFilter, setTagsFilter])

  const handleLinkContractor = useCallback(async () => {
    if (!selectedContractorIds) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        AssignContractorsToCustomer.request({
          partnerDriverIds: selectedContractorIds,
          customerId: selectedMCCustomerId,
        }),
      )

      refreshContractorsAvailableForAssign()
      refreshLinkedContractors()

      showToast('Contractor(s) successfully assigned.')
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [selectedContractorIds])

  return (
    <AssignContractorsTableContext.Provider value={assignContractorsContextValue}>
      <AssignContractorsHeader />
      <GridTable<LinkableSubcontractor>
        rows={contractors}
        isLoading={isLoading}
        columns={assignContractorsColumns}
        paginationModel={paginationModel}
        pageSizeOptions={assignContractorsPageSizeOptions}
        rowCount={totalContractors}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        getRowId={getRowId}
        EmptyElement={EmptyAssignContractors}
        rowSelectionModel={selectedContractorIds}
        onRowSelectionModelChange={handleRowSelectionChange}
        className={tw`!h-[70vh]`}
        checkboxSelection
      />
      <div css={tw`flex justify-between items-center px-4 pt-3.5 pb-4 border-t bg-white`}>
        <Typography variant="subtitle1" color={colors.SHADES_GREY_400}>
          {selectedContractorIds.length} Selected
        </Typography>
        <Button
          color="primary"
          size="small"
          disabled={isLoading}
          leftIcon={<CirclePlusMinor width={20} height={20} fill={colors.BASICS_WHITE} />}
          onClick={handleLinkContractor}
        >
          Link contractor(s)
        </Button>
      </div>
    </AssignContractorsTableContext.Provider>
  )
}

export const AssignContractorsTable = memo(AssignContractorsTableComponent)
